<template>
  <div class="agenda-list-page">
    <div class="agenda-toolbar">
      <FilterFields :fields="filterFields" />
      <div class="agenda-toolbar-actions">
        <button
          class="btn btn-light agenda-action-button"
          v-b-tooltip.hover.bottom="'Hoje'"
          @click="filtrarHoje"
        >
          <CalendarToday :size="20" />
        </button>
        <button
          class="btn btn-light agenda-action-button"
          v-b-tooltip.hover.bottom="'Bloqueio de agenda'"
          @click="openBloqueioModal"
        >
          <CalendarLock :size="20" />
        </button>
        <button
          class="btn btn-light agenda-action-button"
          v-b-tooltip.hover.bottom="'Configurações'"
          @click="openConfiguracaoModal"
        >
          <Cog :size="20" />
        </button>
        <button
          class="btn btn-light agenda-action-button"
          v-b-tooltip.hover.bottom="'Gerar link'"
          @click="openLinkModal"
        >
          <LinkVariant :size="20" />
        </button>
      </div>
    </div>

    <div class="card card-custom card-stretch gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">
            Agendamentos
          </span>
        </h3>
        <div class="card-toolbar">
          <router-link
            :to="{ name: 'crmAgendaVisual' }"
            class="btn btn-light-primary font-weight-bolder font-size-sm mr-2"
          >
            <CalendarCheck :size="18" class="mr-1" />
            Ver agendamentos
          </router-link>
          <router-link
            :to="{ name: 'crmAgendaNovo' }"
            class="btn btn-dark font-weight-bolder font-size-sm"
          >
            Novo Agendamento
          </router-link>
          <button
            class="btn btn-dark font-weight-bolder font-size-sm ml-2"
            @click="printData"
          >
            <Printer :size="18" />
          </button>
        </div>
      </div>

      <div class="card-body pt-0 pb-3">
        <div v-if="loadingList" class="agenda-local-loading">
          <b-spinner small class="mr-2" />
          Carregando agendamentos...
        </div>

        <div v-else-if="listError" class="alert alert-light-danger">
          <div class="d-flex align-items-center justify-content-between">
            <span>{{ listError }}</span>
            <button
              type="button"
              class="btn btn-sm btn-light-danger font-weight-bold"
              @click="loadAgendamentos"
            >
              Tentar novamente
            </button>
          </div>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-striped agenda-table">
            <thead>
              <tr>
                <th
                  v-for="field in fields"
                  :key="field.field"
                  :style="{ width: field.width }"
                >
                  <button
                    v-if="field.sortable"
                    type="button"
                    class="agenda-sort-button"
                    @click="sortBy(field.field)"
                  >
                    {{ field.label }}
                    <span>{{ sortIcon(field.field) }}</span>
                  </button>
                  <span v-else>{{ field.label }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="agendamentos.length === 0">
                <td
                  :colspan="fields.length"
                  class="text-center text-muted py-10"
                >
                  Nenhum registro encontrado
                </td>
              </tr>
              <tr
                v-for="agendamento in agendamentos"
                :key="
                  agendamento.chave_agendamento || agendamento.id_agendamento
                "
              >
                <td>
                  <strong>{{ formatDateTime(agendamento.data_inicio) }}</strong>
                  <small class="text-muted d-block">
                    {{ formatTime(agendamento.data_inicio) }} -
                    {{ formatTime(agendamento.data_fim) }}
                  </small>
                </td>
                <td>
                  <strong>{{ agendamento.cliente || "-" }}</strong>
                  <small class="text-muted d-block" v-if="agendamento.titulo">
                    {{ agendamento.titulo }}
                  </small>
                </td>
                <td>{{ responsavelLabel(agendamento) }}</td>
                <td>{{ agendamento.duracao_minutos }} min</td>
                <td>
                  <span :class="statusClass(agendamento.status)">
                    {{ statusLabel(agendamento.status) }}
                  </span>
                  <span
                    class="agenda-origin-badge"
                    :class="origemAgendamentoClass(agendamento)"
                  >
                    {{ origemAgendamentoLabel(agendamento) }}
                  </span>
                  <span
                    v-if="agendamento.reagendado"
                    class="agenda-reschedule-badge"
                    v-b-tooltip.hover.bottom="reagendamentoTooltip(agendamento)"
                  >
                    R
                  </span>
                </td>
                <td>
                  <div class="action-buttons">
                    <router-link
                      v-if="isAgendamentoOperacional(agendamento)"
                      class="btn btn-sm btn-info btn-hover-info btn-icon mr-2"
                      v-b-tooltip.hover.bottom="'Editar'"
                      :to="{
                        name: 'crmAgendaEditar',
                        params: { id: agendamento.id_agendamento },
                      }"
                    >
                      <Pencil :size="16" />
                    </router-link>
                    <b-dropdown
                      v-if="mostrarAcoesOperacionais(agendamento)"
                      class="agenda-action-dropdown"
                      variant="light-primary"
                      size="sm"
                      right
                      no-caret
                      boundary="window"
                      toggle-class="btn-icon agenda-action-dropdown-toggle"
                    >
                      <template #button-content>
                        <DotsVertical :size="16" />
                      </template>
                      <b-dropdown-item
                        v-if="canConfirmarAgendamento(agendamento)"
                        @click="confirmarAgendamento(agendamento)"
                      >
                        Confirmar
                      </b-dropdown-item>
                      <b-dropdown-item
                        @click="reagendarAgendamento(agendamento)"
                      >
                        Reagendar
                      </b-dropdown-item>
                      <b-dropdown-divider />
                      <b-dropdown-item
                        class="text-danger"
                        @click="cancelarAgendamento(agendamento)"
                      >
                        Cancelar
                      </b-dropdown-item>
                    </b-dropdown>
                    <button
                      v-if="mostrarConcluirOperacional(agendamento)"
                      @click="alterarStatus(agendamento, 'concluido')"
                      class="btn btn-sm btn-success btn-icon mr-2"
                      v-b-tooltip.hover.bottom="'Concluir'"
                    >
                      <CheckCircle :size="16" />
                    </button>
                    <button
                      @click="shareToWhatsapp(agendamento)"
                      class="btn btn-sm btn-success btn-icon mr-2"
                      v-b-tooltip.hover.bottom="'Enviar resumo por WhatsApp'"
                    >
                      <Whatsapp :size="16" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="agenda-pagination">
            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              class="agenda-per-page"
              @input="changePerPage"
            />
            <div class="agenda-pagination-actions">
              <button
                type="button"
                class="btn btn-light font-weight-bold mr-2"
                :disabled="page <= 1"
                @click="changePage(page - 1)"
              >
                Voltar
              </button>
              <span class="text-muted">
                Página {{ page }} de {{ lastPage }}
              </span>
              <button
                type="button"
                class="btn btn-light font-weight-bold ml-2"
                :disabled="page >= lastPage"
                @click="changePage(page + 1)"
              >
                Próximo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      id="agendamentoListaBloqueioModal"
      centered
      title="Bloqueio de agenda"
      @ok="salvarBloqueio"
      ok-title="Bloquear"
      cancel-title="Cancelar"
    >
      <b-form-group v-if="currentUser.admin" label="Usuário">
        <VueSelect
          label="nome"
          :filterable="false"
          :options="usuarios"
          v-model="usuarioAgenda"
          @search="onSearchUsuario"
          :clearSearchOnSelect="true"
        >
          <template slot="no-options">Informe o nome do usuário</template>
          <template slot="option" slot-scope="option">
            {{ usuarioLabel(option) }}
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ usuarioLabel(option) }}
          </template>
        </VueSelect>
      </b-form-group>
      <b-form-group label="Início">
        <b-form-input
          type="datetime-local"
          v-model="bloqueioForm.data_inicio"
        />
      </b-form-group>
      <b-form-group label="Fim">
        <b-form-input type="datetime-local" v-model="bloqueioForm.data_fim" />
      </b-form-group>
      <b-form-group label="Motivo">
        <b-form-textarea rows="3" v-model="bloqueioForm.motivo" />
      </b-form-group>
    </b-modal>

    <b-modal
      id="agendamentoListaConfiguracaoModal"
      centered
      size="lg"
      title="Configurações da agenda"
      @ok="salvarConfiguracao"
      ok-title="Salvar"
      cancel-title="Cancelar"
    >
      <b-form-group v-if="currentUser.admin" label="Usuário">
        <VueSelect
          label="nome"
          :filterable="false"
          :options="usuarios"
          v-model="usuarioAgenda"
          @input="openConfiguracaoModal"
          @search="onSearchUsuario"
          :clearSearchOnSelect="true"
        >
          <template slot="no-options">Informe o nome do usuário</template>
          <template slot="option" slot-scope="option">
            {{ usuarioLabel(option) }}
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ usuarioLabel(option) }}
          </template>
        </VueSelect>
      </b-form-group>

      <div v-for="dia in diasSemana" :key="dia.key" class="agenda-config-day">
        <div class="agenda-config-day-header">
          <strong>{{ dia.label }}</strong>
          <button
            type="button"
            class="btn btn-sm btn-light-primary font-weight-bold"
            @click="addExpedienteBloco(dia.key)"
          >
            Adicionar
          </button>
        </div>
        <div
          v-for="(bloco, index) in configForm.expedientes[dia.key]"
          :key="`${dia.key}-${index}`"
          class="agenda-config-block"
        >
          <b-form-input type="time" v-model="bloco.inicio" />
          <b-form-input type="time" v-model="bloco.fim" />
          <button
            type="button"
            class="btn btn-sm btn-light-danger font-weight-bold"
            @click="removeExpedienteBloco(dia.key, index)"
          >
            Remover
          </button>
        </div>
        <small
          v-if="configForm.expedientes[dia.key].length === 0"
          class="text-muted"
        >
          Sem expediente
        </small>
      </div>
    </b-modal>

    <b-modal
      id="agendamentoListaLinkModal"
      centered
      size="lg"
      title="Gerar link de agenda"
      @ok="gerarLinkAgenda"
      ok-title="Gerar link"
      cancel-title="Cancelar"
    >
      <b-form-group v-if="currentUser.admin" label="Usuário">
        <VueSelect
          label="nome"
          :filterable="false"
          :options="usuarios"
          v-model="usuarioAgenda"
          @search="onSearchUsuario"
          :clearSearchOnSelect="true"
        >
          <template slot="no-options">Informe o nome do usuário</template>
          <template slot="option" slot-scope="option">
            {{ usuarioLabel(option) }}
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ usuarioLabel(option) }}
          </template>
        </VueSelect>
      </b-form-group>
      <b-row>
        <b-col md="6">
          <b-form-group label="Cliente">
            <VueSelect
              label="nome"
              :filterable="false"
              :options="clientes"
              v-model="linkForm.cliente"
              @search="onSearchCliente"
              :clearSearchOnSelect="true"
            >
              <template slot="no-options">Informe o nome do cliente</template>
            </VueSelect>
            <button
              type="button"
              class="btn btn-light-primary btn-sm font-weight-bold mt-3"
              @click="openNovoClienteLinkModal"
            >
              Novo Cliente
            </button>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="4">
          <b-form-group label="Data inicial">
            <b-form-input type="date" v-model="linkForm.data_inicio" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Data final">
            <b-form-input type="date" v-model="linkForm.data_fim" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Duração">
            <b-form-input
              type="number"
              min="1"
              max="1440"
              v-model.number="linkForm.duracao_minutos"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <div
        v-if="linkGerado"
        class="alert alert-light-success agenda-link-success mb-0"
      >
        <strong>Link gerado com sucesso.</strong>
        <button
          type="button"
          class="btn btn-light-primary btn-sm font-weight-bold"
          @click="copiarLinkGerado"
        >
          <ContentCopy :size="16" class="mr-1" />
          Copiar link
        </button>
      </div>
    </b-modal>

    <b-modal
      id="novoClienteAgendamentoLinkModal"
      centered
      size="xl"
      title="Novo Cliente"
      hide-footer
      body-class="p-0 novo-cliente-agendamento-modal-body"
      @hidden="resetNovoClienteLinkModal"
    >
      <PessoaForm
        url="/pessoa/create"
        :pessoa="novaPessoa"
        :hide-tipo-pessoa="true"
        :force-cliente="true"
        :redirect-on-save="false"
        @saved="handleNovoClienteLinkSaved"
        @cancel="$bvModal.hide('novoClienteAgendamentoLinkModal')"
      />
    </b-modal>

    <SendToWppModal
      :data="dataToWhatsapp"
      :openModal="wppOpenModal"
      @handleOpenModal="(openModal) => (wppOpenModal = openModal)"
      module="agendamentos"
    />
  </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import { mapGetters } from "vuex";
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import FilterFields from "@/components/FilterFields";
import { filtersStore } from "@/components/FilterFields/filtersStore";
import { SistemaMixin } from "@/modules/Application";
import SendToWppModal from "@/components/SendToWppModal";
import PessoaForm from "@/modules/Pessoa/pages/Form.vue";
import PessoaData from "@/modules/Pessoa/pages/PessoaData";
import Pencil from "vue-material-design-icons/Pencil.vue";
import CheckCircle from "vue-material-design-icons/CheckCircle.vue";
import CalendarCheck from "vue-material-design-icons/CalendarCheck.vue";
import CalendarLock from "vue-material-design-icons/CalendarLock.vue";
import CalendarToday from "vue-material-design-icons/CalendarToday.vue";
import ContentCopy from "vue-material-design-icons/ContentCopy.vue";
import Cog from "vue-material-design-icons/Cog.vue";
import DotsVertical from "vue-material-design-icons/DotsVertical.vue";
import LinkVariant from "vue-material-design-icons/LinkVariant.vue";
import Printer from "vue-material-design-icons/Printer.vue";
import Whatsapp from "vue-material-design-icons/Whatsapp.vue";

export default {
  name: "app-crm-agendamentos",
  mixins: [SistemaMixin],
  components: {
    FilterFields,
    SendToWppModal,
    VueSelect,
    PessoaForm,
    Pencil,
    CheckCircle,
    CalendarCheck,
    CalendarLock,
    CalendarToday,
    ContentCopy,
    Cog,
    DotsVertical,
    LinkVariant,
    Printer,
    Whatsapp,
  },
  data() {
    return {
      filtersStore,
      loadingList: false,
      listError: "",
      agendamentos: [],
      totalRecords: 0,
      page: 1,
      perPage: 10,
      lastPage: 1,
      dataToWhatsapp: {},
      wppOpenModal: false,
      usuarios: [],
      usuarioAgenda: null,
      clientes: [],
      novaPessoa: new PessoaData({}),
      bloqueioForm: {
        data_inicio: "",
        data_fim: "",
        motivo: "",
      },
      configForm: {
        expedientes: {
          0: [],
          1: [{ inicio: "08:00", fim: "18:00" }],
          2: [{ inicio: "08:00", fim: "18:00" }],
          3: [{ inicio: "08:00", fim: "18:00" }],
          4: [{ inicio: "08:00", fim: "18:00" }],
          5: [{ inicio: "08:00", fim: "18:00" }],
          6: [{ inicio: "08:00", fim: "18:00" }],
        },
      },
      linkForm: {
        cliente: null,
        data_inicio: moment().format("YYYY-MM-DD"),
        data_fim: moment().format("YYYY-MM-DD"),
        duracao_minutos: 30,
      },
      linkGerado: "",
      diasSemana: [
        { key: "0", label: "Domingo" },
        { key: "1", label: "Segunda-feira" },
        { key: "2", label: "Terça-feira" },
        { key: "3", label: "Quarta-feira" },
        { key: "4", label: "Quinta-feira" },
        { key: "5", label: "Sexta-feira" },
        { key: "6", label: "Sábado" },
      ],
      sort: {
        field: "",
        type: "",
      },
      perPageOptions: [10, 20, 30, 40, 50, 100],
      filterFields: [
        { type: "text", label: "Agendamento", field: "titulo" },
        { type: "text", label: "Cliente", field: "cliente" },
        { type: "text", label: "Responsável", field: "usuario" },
        { type: "date", label: "Data", field: "data_inicio" },
        {
          type: "select",
          label: "Status",
          field: "status",
          options: [
            "Todos",
            "agendado",
            "confirmado",
            "em_atendimento",
            "concluido",
            "cancelado",
            "nao_compareceu",
          ],
        },
      ],
      fields: [
        {
          label: "Data",
          field: "data_inicio",
          sortable: true,
          width: "17%",
        },
        {
          label: "Cliente",
          field: "cliente",
          sortable: true,
          width: "20%",
        },
        {
          label: "Responsável",
          field: "usuario",
          sortable: true,
          width: "17%",
        },
        {
          label: "Duração",
          field: "duracao_minutos",
          sortable: true,
          width: "8%",
        },
        {
          label: "Status",
          field: "status",
          sortable: true,
          width: "10%",
        },
        {
          label: "Ações",
          field: "actions",
          sortable: false,
          width: "14%",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    currentUserAgendaUser() {
      return {
        id_usuario: this.currentUser?.id_usuario,
        nome: this.currentUser?.nome || this.currentUser?.usuario,
        usuario: this.currentUser?.usuario,
      };
    },
    agendaTargetUser() {
      return this.usuarioAgenda || this.currentUserAgendaUser;
    },
  },
  mounted() {
    this.page = 1;
    this.perPage = this.filtersStore.agendamentos.per_page || 10;
    this.filtersStore.agendamentos.page = 1;
    this.$store.dispatch("setLoading", false);
    this.loadAgendamentos();
  },
  beforeDestroy() {
    this.$store.dispatch("setLoading", false);
  },
  watch: {
    "filtersStore.agendamentos": {
      handler: _.debounce(function () {
        this.page = this.filtersStore.agendamentos.page || 1;
        this.perPage = this.filtersStore.agendamentos.per_page || 10;
        this.loadAgendamentos();
      }, 250),
      deep: true,
    },
  },
  methods: {
    async loadAgendamentos() {
      this.loadingList = true;
      this.listError = "";
      this.$store.dispatch("setLoading", false);

      try {
        const payload = {
          page: this.page,
          per_page: this.perPage,
          columnFilters: this.filtersStore.agendamentos.filters || {},
          sort: this.sort,
        };

        if (!this.currentUser.admin) {
          payload.id_usuario = this.currentUser?.id_usuario;
        }

        const { data } = await this.$http.post(
          "/crm/agendamentos/lista",
          payload
        );

        this.agendamentos = data.data || [];
        this.totalRecords = data.total || 0;
        this.lastPage = data.last_page || 1;
        this.page = data.current_page || 1;

        if (this.totalRecords > 0 && this.page > this.lastPage) {
          this.changePage(this.lastPage);
        }
      } catch (error) {
        this.agendamentos = [];
        this.totalRecords = 0;
        this.lastPage = 1;
        this.listError =
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível carregar os agendamentos.";
      } finally {
        this.loadingList = false;
        this.$store.dispatch("setLoading", false);
      }
    },
    changePage(page) {
      this.page = Math.max(page, 1);
      this.filtersStore.agendamentos.page = this.page;
    },
    changePerPage() {
      this.page = 1;
      this.filtersStore.agendamentos.per_page = this.perPage;
      this.filtersStore.agendamentos.page = 1;
    },
    sortBy(field) {
      if (this.sort.field === field) {
        this.sort.type = this.sort.type === "asc" ? "desc" : "asc";
      } else {
        this.sort = {
          field,
          type: "asc",
        };
      }

      this.page = 1;
      this.filtersStore.agendamentos.page = 1;
      this.loadAgendamentos();
    },
    sortIcon(field) {
      if (this.sort.field !== field) return "";
      return this.sort.type === "asc" ? "▲" : "▼";
    },
    filtrarHoje() {
      const today = moment().format("YYYY-MM-DD");
      this.filtersStore.agendamentos.filters = {
        ...this.filtersStore.agendamentos.filters,
        range_data_inicio: [today],
      };
      this.filtersStore.agendamentos.page = 1;
    },
    usuarioLabel(usuario) {
      return usuario?.nome || usuario?.usuario || "-";
    },
    async ensureUsuarioAgenda() {
      if (this.currentUser.admin && this.usuarios.length === 0) {
        await this.loadUsuarios();
      }

      if (!this.usuarioAgenda) {
        this.usuarioAgenda = this.currentUser.admin
          ? this.usuarios[0] || this.currentUserAgendaUser
          : this.currentUserAgendaUser;
      }
    },
    async loadUsuarios(search = "") {
      if (!this.currentUser.admin) {
        this.usuarioAgenda = this.currentUserAgendaUser;
        return;
      }

      const { data } = await this.$http.post("/usuario/get-usuarios", {
        search,
      });
      this.usuarios = Array.isArray(data) ? data : data.data || [];

      if (!this.usuarioAgenda && this.usuarios.length) {
        this.usuarioAgenda = this.usuarios[0];
      }
    },
    onSearchUsuario(search, loading) {
      if (search.length) {
        loading(true);
        this.searchUsuario(loading, search, this);
      }
    },
    searchUsuario: _.debounce(async (loading, search, vm) => {
      await vm.loadUsuarios(search);
      loading(false);
    }, 350),
    async openBloqueioModal() {
      await this.ensureUsuarioAgenda();
      const today = moment().format("YYYY-MM-DD");
      this.bloqueioForm = {
        data_inicio: `${today}T08:00`,
        data_fim: `${today}T09:00`,
        motivo: "",
      };
      this.$bvModal.show("agendamentoListaBloqueioModal");
    },
    async salvarBloqueio(event) {
      event.preventDefault();
      await this.ensureUsuarioAgenda();

      if (!this.bloqueioForm.motivo || this.bloqueioForm.motivo.length < 3) {
        this.$toast.warning("Informe o motivo do bloqueio.");
        return;
      }

      await this.$http.post("/agendamentos/bloqueios", {
        id_usuario: this.agendaTargetUser?.id_usuario,
        data_inicio: this.datetimeLocalToApi(this.bloqueioForm.data_inicio),
        data_fim: this.datetimeLocalToApi(this.bloqueioForm.data_fim),
        motivo: this.bloqueioForm.motivo,
      });

      this.$bvModal.hide("agendamentoListaBloqueioModal");
      this.$toast.success("Agenda bloqueada!");
      this.loadAgendamentos();
    },
    async openConfiguracaoModal() {
      await this.ensureUsuarioAgenda();

      const { data } = await this.$http.post("/agendamentos/configuracao", {
        id_usuario: this.agendaTargetUser?.id_usuario,
      });

      const configuracao = data.data || data;
      this.configForm = {
        expedientes: this.ensureExpedientes(configuracao.expedientes),
      };
      this.$bvModal.show("agendamentoListaConfiguracaoModal");
    },
    async salvarConfiguracao(event) {
      event.preventDefault();
      await this.ensureUsuarioAgenda();

      await this.$http.post("/agendamentos/configuracao/salvar", {
        id_usuario: this.agendaTargetUser?.id_usuario,
        expedientes: this.configForm.expedientes,
      });

      this.$bvModal.hide("agendamentoListaConfiguracaoModal");
      this.$toast.success("Configuração salva!");
    },
    ensureExpedientes(expedientes = {}) {
      return this.diasSemana.reduce((carry, dia) => {
        carry[dia.key] = (expedientes[dia.key] || []).map((bloco) => ({
          inicio: bloco.inicio,
          fim: bloco.fim,
        }));

        return carry;
      }, {});
    },
    addExpedienteBloco(dia) {
      this.configForm.expedientes[dia].push({
        inicio: "08:00",
        fim: "18:00",
      });
    },
    removeExpedienteBloco(dia, index) {
      this.configForm.expedientes[dia].splice(index, 1);
    },
    async openLinkModal() {
      await this.ensureUsuarioAgenda();
      const today = moment().format("YYYY-MM-DD");
      this.linkGerado = "";
      this.linkForm = {
        cliente: null,
        data_inicio: today,
        data_fim: today,
        duracao_minutos: 30,
      };
      this.$bvModal.show("agendamentoListaLinkModal");
    },
    async gerarLinkAgenda(event) {
      event.preventDefault();
      await this.ensureUsuarioAgenda();

      const duracao = Number(this.linkForm.duracao_minutos || 0);

      if (!this.linkForm.data_inicio || !this.linkForm.data_fim) {
        this.$toast.warning("Informe o período do link.");
        return;
      }

      if (this.linkForm.data_inicio > this.linkForm.data_fim) {
        this.$toast.warning(
          "A data final deve ser igual ou posterior à inicial."
        );
        return;
      }

      if (!Number.isInteger(duracao) || duracao < 1 || duracao > 1440) {
        this.$toast.warning("Informe uma duração entre 1 minuto e 24 horas.");
        return;
      }

      const { data } = await this.$http.post("/agendamentos/link", {
        id_usuario: this.agendaTargetUser?.id_usuario,
        id_pessoa: this.linkForm.cliente?.id_pessoa || null,
        origem: "crm_agenda",
        duracao_minutos: duracao,
        data_inicio: `${this.linkForm.data_inicio} 00:00:00`,
        data_fim: `${this.linkForm.data_fim} 23:59:59`,
      });

      this.linkGerado = data.data?.url_publica || data.url_publica;
      this.$toast.success("Link gerado!");
    },
    async copiarLinkGerado() {
      if (!this.linkGerado) return;

      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(this.linkGerado);
        } else {
          await this.$copyText(this.linkGerado);
        }
        this.$toast.success("Link copiado!");
      } catch (error) {
        this.$toast.error("Não foi possível copiar o link.");
      }
    },
    onSearchCliente(search, loading) {
      if (search.length) {
        loading(true);
        this.searchCliente(loading, search, this);
      }
    },
    searchCliente: _.debounce(async (loading, search, vm) => {
      vm.clientes = await vm.getClientes(search);
      loading(false);
    }, 350),
    openNovoClienteLinkModal() {
      this.novaPessoa = new PessoaData({});
      this.$bvModal.show("novoClienteAgendamentoLinkModal");
    },
    resetNovoClienteLinkModal() {
      this.novaPessoa = new PessoaData({});
    },
    async handleNovoClienteLinkSaved({ response, form }) {
      try {
        const cliente = await this.resolveClienteCriado(response, form);

        if (!cliente?.id_pessoa) {
          this.$toast.warning(
            "Cliente cadastrado, mas não foi possível selecioná-lo automaticamente."
          );
          this.$bvModal.hide("novoClienteAgendamentoLinkModal");
          return;
        }

        this.clientes = [cliente];
        this.linkForm.cliente = cliente;
        this.$bvModal.hide("novoClienteAgendamentoLinkModal");
        this.$toast.success("Cliente cadastrado e selecionado!");
      } catch (error) {
        this.$toast.error(
          "Cliente cadastrado, mas houve erro ao selecionar no link."
        );
      }
    },
    async resolveClienteCriado(response, form) {
      const responseData = response?.data ?? response;
      const id_pessoa = responseData?.id_pessoa ?? responseData?.id;
      const cliente =
        responseData?.pessoa ??
        responseData?.cliente ??
        responseData?.data ??
        responseData;

      if (cliente?.id_pessoa) return cliente;

      if (id_pessoa) {
        const { data } = await this.$http.post("/pessoa/find", { id_pessoa });
        return data?.pessoa ?? data?.data ?? data;
      }

      if (!form?.nome || form.nome.trim().length < 2) return null;

      const clientes = await this.getClientes(form.nome.trim());
      return clientes.find((item) => item.nome === form.nome) ?? clientes[0];
    },
    datetimeLocalToApi(value) {
      return moment(value).format("YYYY-MM-DD HH:mm:ss");
    },
    printData() {
      const labels = [
        { label: "Data", field: "data_formatada" },
        { label: "Cliente", field: "cliente" },
        { label: "Profissional", field: "profissional" },
        { label: "Duração", field: "duracao_formatada" },
        { label: "Status", field: "status_formatado" },
      ];
      const data = this.agendamentos.map((agendamento) => ({
        data_formatada: `${this.formatDateTime(
          agendamento.data_inicio
        )} ${this.formatTime(agendamento.data_inicio)} - ${this.formatTime(
          agendamento.data_fim
        )}`,
        cliente: agendamento.cliente || "-",
        profissional: this.responsavelLabel(agendamento),
        duracao_formatada: `${agendamento.duracao_minutos || "-"} min`,
        status_formatado: this.statusLabel(agendamento.status),
      }));

      this.$router.push({
        name: "visualizarImpressao",
        params: {
          data,
          labels,
          title: "Agendamentos",
        },
      });
    },
    canConfirmarAgendamento(agendamento) {
      return ![
        "confirmado",
        "concluido",
        "cancelado",
        "nao_compareceu",
      ].includes(agendamento.status);
    },
    async confirmarAgendamento(agendamento) {
      if (!this.isAgendamentoOperacional(agendamento)) {
        this.$toast.info("Use a Agenda CRM para editar este compromisso.");
        return;
      }

      const result = await this.$swal.fire({
        icon: "question",
        title: "Confirmar este agendamento?",
        showCancelButton: true,
        confirmButtonText: "Sim, confirmar",
        cancelButtonText: "Não",
      });

      if (!result.isConfirmed) return;

      await this.$http.post("/agendamentos/aceitar", {
        id_agendamento: agendamento.id_agendamento,
      });

      this.$toast.success("Agendamento confirmado!");
      this.loadAgendamentos();
    },
    async cancelarAgendamento(agendamento) {
      if (!this.isAgendamentoOperacional(agendamento)) {
        this.$toast.info("Use a Agenda CRM para cancelar este compromisso.");
        return;
      }

      const result = await this.$swal.fire({
        icon: "warning",
        title: "Cancelar agendamento",
        input: "textarea",
        inputLabel: "Motivo do cancelamento",
        inputPlaceholder: "Descreva o motivo",
        inputAttributes: {
          maxlength: 500,
        },
        showCancelButton: true,
        confirmButtonText: "Cancelar agendamento",
        cancelButtonText: "Voltar",
        preConfirm: (motivo) => {
          if (!motivo || motivo.trim().length < 3) {
            this.$swal.showValidationMessage(
              "Informe um motivo com pelo menos 3 caracteres."
            );
            return false;
          }

          return motivo.trim();
        },
      });

      if (!result.isConfirmed) return;

      await this.$http.post("/crm/agendamentos/cancelar", {
        id_agendamento: agendamento.id_agendamento,
        motivo_cancelamento: result.value,
      });

      this.$toast.success("Agendamento cancelado!");
      this.loadAgendamentos();
    },
    async reagendarAgendamento(agendamento) {
      if (!this.isAgendamentoOperacional(agendamento)) {
        this.$toast.info("Use a Agenda CRM para reagendar este compromisso.");
        return;
      }

      const result = await this.$swal.fire({
        icon: "info",
        title: "Reagendar agendamento",
        input: "textarea",
        inputLabel: "Motivo do reagendamento",
        inputPlaceholder: "Descreva o motivo",
        inputAttributes: {
          maxlength: 500,
        },
        showCancelButton: true,
        confirmButtonText: "Continuar",
        cancelButtonText: "Voltar",
        preConfirm: (motivo) => {
          if (!motivo || motivo.trim().length < 3) {
            this.$swal.showValidationMessage(
              "Informe um motivo com pelo menos 3 caracteres."
            );
            return false;
          }

          return motivo.trim();
        },
      });

      if (!result.isConfirmed) return;

      this.$router.push({
        name: "crmAgendaEditar",
        params: { id: agendamento.id_agendamento },
        query: { reagendar: "1", motivo: result.value },
      });
    },
    async alterarStatus(agendamento, status, confirmar = true) {
      if (!this.isAgendamentoOperacional(agendamento)) {
        this.$toast.info("Use a Agenda CRM para alterar este compromisso.");
        return;
      }

      if (confirmar) {
        const result = await this.$swal.fire({
          icon: status === "cancelado" ? "warning" : "info",
          title: `${this.statusLabel(status)} este agendamento?`,
          showCancelButton: true,
          confirmButtonText: "Sim",
          cancelButtonText: "Não",
        });

        if (!result.isConfirmed) return;
      }

      await this.$http.post("/crm/agendamentos/concluir", {
        id_agendamento: agendamento.id_agendamento,
        status,
      });

      this.$toast.success("Agendamento atualizado!");
      this.loadAgendamentos();
    },
    shareToWhatsapp(agendamento) {
      this.dataToWhatsapp = agendamento;
      this.wppOpenModal = true;
    },
    responsavelLabel(agendamento) {
      return agendamento.usuario || agendamento.tecnico || "-";
    },
    isAgendamentoOperacional(agendamento) {
      return agendamento?.agenda_origem !== "crm_agenda";
    },
    mostrarAcoesOperacionais(agendamento) {
      return (
        this.isAgendamentoOperacional(agendamento) &&
        !this.isStatusFinal(agendamento.status)
      );
    },
    mostrarConcluirOperacional(agendamento) {
      return (
        this.isAgendamentoOperacional(agendamento) &&
        agendamento.status === "confirmado"
      );
    },
    origemAgendamentoLabel(agendamento) {
      return agendamento?.agenda_origem === "crm_agenda"
        ? "Agenda CRM"
        : "Operacional";
    },
    origemAgendamentoClass(agendamento) {
      return agendamento?.agenda_origem === "crm_agenda"
        ? "agenda-origin-crm"
        : "agenda-origin-operacional";
    },
    reagendamentoTooltip(agendamento) {
      const inicio = agendamento.reagendado_de_inicio
        ? moment(agendamento.reagendado_de_inicio).format("DD/MM/YYYY HH:mm")
        : "-";
      const fim = agendamento.reagendado_de_fim
        ? moment(agendamento.reagendado_de_fim).format("HH:mm")
        : "-";
      const motivo = agendamento.motivo_reagendamento || "Motivo não informado";

      return `Original: ${inicio} - ${fim}. Motivo: ${motivo}`;
    },
    isStatusFinal(status) {
      return ["concluido", "cancelado", "nao_compareceu"].includes(status);
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY") : "-";
    },
    formatTime(value) {
      return value ? moment(value).format("HH:mm") : "--:--";
    },
    statusLabel(status) {
      const labels = {
        agendado: "Agendado",
        pendente: "Pendente",
        confirmado: "Confirmado",
        em_atendimento: "Em atendimento",
        concluido: "Concluído",
        cancelado: "Cancelado",
        nao_compareceu: "Não compareceu",
      };

      return labels[status] || status;
    },
    statusClass(status) {
      const classes = {
        agendado: "label label-lg label-inline label-light-primary",
        pendente: "label label-lg label-inline label-light-warning",
        confirmado: "label label-lg label-inline label-light-success",
        em_atendimento: "label label-lg label-inline label-light-info",
        concluido: "label label-lg label-inline label-light-success",
        cancelado: "label label-lg label-inline label-light-danger",
        nao_compareceu: "label label-lg label-inline label-light-warning",
      };

      return classes[status] || "label label-lg label-inline label-light";
    },
  },
};
</script>

<style scoped>
.agenda-list-page {
  position: relative;
}

.agenda-toolbar {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}

.agenda-toolbar > div {
  flex: 1;
}

.agenda-toolbar > .agenda-toolbar-actions {
  flex: 0 0 auto;
}

.agenda-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
}

.agenda-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  color: #f06f61;
}

.agenda-config-section,
.agenda-config-day {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 14px;
  background: #fafafa;
}

.agenda-config-day-header,
.agenda-config-block {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.agenda-config-day-header {
  justify-content: space-between;
}

.agenda-config-block .form-control {
  max-width: 150px;
}

.agenda-import-box {
  border-top: 1px solid #ebedf3;
  margin-top: 14px;
  padding-top: 14px;
}

.agenda-link-success {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.agenda-local-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  color: #7e8299;
  font-weight: 600;
}

.agenda-table th {
  color: #b5b5c3;
  font-size: 0.9rem;
  font-weight: 600;
}

.agenda-sort-button {
  border: 0;
  background: transparent;
  padding: 0;
  color: inherit;
  font: inherit;
  font-weight: 600;
}

.agenda-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0 6px;
}

.agenda-per-page {
  max-width: 90px;
}

.agenda-pagination-actions {
  display: flex;
  align-items: center;
}

.action-buttons {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.action-buttons .btn-icon,
.agenda-action-dropdown {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  margin-right: 0 !important;
}

.agenda-action-dropdown ::v-deep .agenda-action-dropdown-toggle {
  width: 32px;
  height: 32px;
  padding: 0;
}

.agenda-action-dropdown ::v-deep .dropdown-menu {
  min-width: 130px;
}

.agenda-reschedule-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: 6px;
  border-radius: 50%;
  background: #fff4de;
  color: #ffa800;
  font-size: 11px;
  font-weight: 800;
  vertical-align: middle;
}

.agenda-origin-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  padding: 0 8px;
  margin-left: 6px;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  vertical-align: middle;
}

.agenda-origin-crm {
  background: #e1f0ff;
  color: #3699ff;
}

.agenda-origin-operacional {
  background: #f3f6f9;
  color: #7e8299;
}
</style>
